import {
	ImportUploadErrorCode,
	ImportUploadOutcome,
	type LogicalFileType,
	RetrievalMethodType,
	ValidationErrorCode,
} from '@today/types'
import type { ImportUploadState } from '@today/ui-domain'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import { importUploadService } from '@/services/import-upload.service'

/** The refusals a start can answer, in the console's words (TO-392 Contract). */
const REFUSALS: Readonly<Record<string, string>> = {
	[ValidationErrorCode.IMPORT_FILENAME_NOT_ALLOWED]: 'This file name is not allowed for web upload',
	[ValidationErrorCode.IMPORT_FILE_SIZE_EXCEEDED]: 'This file is larger than the web upload limit',
	[ValidationErrorCode.IMPORT_FILE_NOT_ENABLED]: 'This file is not enabled for imports',
	[ValidationErrorCode.IMPORT_TRANSPORT_NOT_ALLOWED]: 'Web upload is not allowed for this file',
	[ValidationErrorCode.IMPORT_DEFINITION_UNUSABLE]: "This file's import definition is not active",
	[ImportUploadErrorCode.IMPORT_DAILY_LIMIT_REACHED]: "Today's web upload limit for this file is reached",
}
const FAILED = 'The upload failed — try again.'

/** The message for a failed step: a known refusal, or the generic failure. */
export const uploadErrorMessage = (error: unknown): string => {
	const code = isAxiosError<{ error?: { code?: string } }>(error) ? error.response?.data?.error?.code : undefined
	return (code && REFUSALS[code]) || FAILED
}

/** Lowercase hex SHA-256 of the file's bytes (WebCrypto). */
const sha256Hex = async (file: Blob): Promise<string> => {
	const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
	return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * One logical file's `web` upload (TO-392 REQ-014): hash the file, start the
 * upload, `PUT` the bytes to the returned URL, complete it, and expose the
 * import's status or the refusal for {@link ImportUploadPanel}.
 */
export function useImportUpload(fileType: LogicalFileType) {
	const [state, setState] = useState<ImportUploadState>('idle')
	const [statusText, setStatusText] = useState<string | null>(null)
	const [message, setMessage] = useState<string | null>(null)

	const upload = async (file: File) => {
		setState('uploading')
		setMessage(null)
		setStatusText(null)
		try {
			const started = await importUploadService.start({
				logicalFile: fileType,
				transport: RetrievalMethodType.WEB,
				filename: file.name,
				sizeBytes: file.size,
				sha256: await sha256Hex(file),
			})
			await importUploadService.putBytes(started.upload, file)
			const done = await importUploadService.complete(started.import.id)
			setStatusText(
				done.outcome === ImportUploadOutcome.DUPLICATE
					? `Already received — ${done.import.status}`
					: done.import.status,
			)
			setState('done')
		} catch (error) {
			setMessage(uploadErrorMessage(error))
			setState('error')
		}
	}

	return { state, statusText, message, upload }
}
